<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'

// 定义食物属性
const props = defineProps<{
  food: {
    id: number
    type: {
      name: string
      image: string
      weight: number
      value: number
    }
    x: number
    y: number
    velocityX: number
    velocityY: number
    rotation: number
    scale: number
  }
}>()

// 扭曲特效状态
const distortionLevel = ref(0)
const glitchActive = ref(false)

// 计算食物样式
const foodStyle = computed(() => {
  return {
    left: `${props.food.x}px`,
    top: `${props.food.y}px`,
    transform: `scale(${props.food.scale}) rotate(${props.food.rotation}deg)`,
    filter: `blur(${distortionLevel.value}px) hue-rotate(${distortionLevel.value * 10}deg)`,
    opacity: glitchActive.value ? 0.7 : 1,
  }
})

// 根据食物类型添加特定效果
const getFoodSpecificStyles = computed(() => {
  const styles: Record<string, string> = {}

  switch (props.food.type.name) {
    case '便便':
      styles.borderRadius = '60% 40% 40% 60% / 40% 60% 40% 60%'
      styles.background = '#7b5d3f'
      break
    case '榴莲':
      styles.borderRadius = '50%'
      styles.background = '#a0b83c'
      styles.boxShadow = '0 0 10px rgba(160, 184, 60, 0.5)'
      break
    case '臭袜子':
      styles.borderRadius = '10px 10px 30px 30px'
      styles.background = '#e5e5e5'
      styles.height = '60px'
      break
    case '电视机':
      styles.borderRadius = '5px'
      styles.background = '#333'
      styles.border = '2px solid #222'
      styles.width = '60px'
      styles.height = '45px'
      break
    case '诺基亚':
      styles.borderRadius = '5px'
      styles.background = '#2c3e50'
      styles.width = '30px'
      styles.height = '50px'
      break
  }

  return styles
})

// 创建下沉波纹效果
const ripples = ref<Array<{ id: number; size: number; opacity: number }>>([])

const createRipple = () => {
  // 随机添加下沉波纹
  if (Math.random() < 0.3) {
    const ripple = {
      id: Date.now(),
      size: 0,
      opacity: 0.8,
    }

    ripples.value.push(ripple)

    // 波纹扩散动画
    const expandRipple = setInterval(() => {
      ripple.size += 5
      ripple.opacity -= 0.05

      if (ripple.opacity <= 0) {
        clearInterval(expandRipple)
        ripples.value = ripples.value.filter((r) => r.id !== ripple.id)
      }
    }, 100)
  }

  // 持续添加波纹
  const timer = setTimeout(createRipple, 500 + Math.random() * 1000)

  // 如果食物消失，清除定时器
  return () => clearTimeout(timer)
}

// 增加下沉时的扭曲效果
const updateDistortion = () => {
  // 随机增加扭曲
  if (Math.random() < 0.2) {
    distortionLevel.value = Math.min(5, distortionLevel.value + 0.5)
  } else {
    distortionLevel.value = Math.max(0, distortionLevel.value - 0.2)
  }

  // 随机故障效果
  if (Math.random() < 0.05) {
    glitchActive.value = true
    setTimeout(() => {
      glitchActive.value = false
    }, 200)
  }

  // 持续更新
  const timer = setTimeout(updateDistortion, 300)

  // 如果食物消失，清除定时器
  return () => clearTimeout(timer)
}

// 生命周期钩子
onMounted(() => {
  const cleanupRipple = createRipple()
  const cleanupDistortion = updateDistortion()

  // 组件卸载时清理定时器
  onUnmounted(() => {
    cleanupRipple()
    cleanupDistortion()
  })
})
</script>

<template>
  <div class="food" :style="foodStyle">
    <!-- 食物主体 -->
    <div class="food-body" :style="getFoodSpecificStyles">
      <!-- 食物内部装饰 -->
      <template v-if="food.type.name === '便便'">
        <div class="poop-texture"></div>
      </template>

      <template v-if="food.type.name === '榴莲'">
        <div class="durian-spikes" v-for="i in 10" :key="i"></div>
      </template>

      <template v-if="food.type.name === '电视机'">
        <div class="tv-screen"></div>
        <div class="tv-antenna" v-for="i in 2" :key="i"></div>
      </template>

      <template v-if="food.type.name === '诺基亚'">
        <div class="nokia-screen"></div>
        <div class="nokia-keypad"></div>
      </template>
    </div>

    <!-- 波纹效果 -->
    <div
      v-for="ripple in ripples"
      :key="ripple.id"
      class="ripple"
      :style="{
        width: `${ripple.size}px`,
        height: `${ripple.size}px`,
        opacity: ripple.opacity,
      }"
    ></div>

    <!-- 故障效果 -->
    <div v-if="glitchActive" class="glitch-effect"></div>
  </div>
</template>

<style scoped>
.food {
  position: absolute;
  z-index: 4;
  transition: filter 0.3s ease;
}

.food-body {
  position: relative;
  width: 40px;
  height: 40px;
  background: #888;
  border-radius: 50%;
  overflow: visible;
}

.ripple {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
  pointer-events: none;
}

.glitch-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(255, 0, 255, 0.2) 10%,
    transparent 20%,
    rgba(0, 255, 255, 0.2) 30%,
    transparent 40%,
    rgba(255, 255, 0, 0.2) 50%,
    transparent 60%,
    rgba(255, 0, 255, 0.2) 70%,
    transparent 80%,
    rgba(0, 255, 255, 0.2) 90%,
    transparent 100%
  );
  mix-blend-mode: screen;
  animation: glitchMove 0.2s steps(2) infinite;
}

/* 食物特定样式 */
.poop-texture {
  position: absolute;
  width: 15px;
  height: 8px;
  background: #5e462e;
  border-radius: 50%;
  top: 10px;
  left: 12px;
  box-shadow:
    5px 15px 0 -2px #5e462e,
    -5px 15px 0 -2px #5e462e;
}

.durian-spikes {
  position: absolute;
  width: 15px;
  height: 5px;
  background: #7d8d00;
  border-radius: 2px;
}

.durian-spikes:nth-child(1) {
  top: 5px;
  left: 50%;
  transform: translateX(-50%) rotate(0deg);
}
.durian-spikes:nth-child(2) {
  top: 15px;
  right: 0;
  transform: rotate(72deg);
}
.durian-spikes:nth-child(3) {
  bottom: 15px;
  right: 0;
  transform: rotate(144deg);
}
.durian-spikes:nth-child(4) {
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
}
.durian-spikes:nth-child(5) {
  bottom: 15px;
  left: 0;
  transform: rotate(216deg);
}
.durian-spikes:nth-child(6) {
  top: 15px;
  left: 0;
  transform: rotate(288deg);
}
.durian-spikes:nth-child(7) {
  top: 10px;
  left: 10px;
  transform: rotate(45deg);
}
.durian-spikes:nth-child(8) {
  top: 10px;
  right: 10px;
  transform: rotate(135deg);
}
.durian-spikes:nth-child(9) {
  bottom: 10px;
  right: 10px;
  transform: rotate(225deg);
}
.durian-spikes:nth-child(10) {
  bottom: 10px;
  left: 10px;
  transform: rotate(315deg);
}

.tv-screen {
  position: absolute;
  width: 80%;
  height: 65%;
  background: #111;
  top: 10%;
  left: 10%;
  overflow: hidden;
}

.tv-screen::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 3px;
  background: rgba(255, 255, 255, 0.2);
  top: 50%;
  animation: tvScan 2s linear infinite;
}

.tv-antenna {
  position: absolute;
  width: 3px;
  height: 15px;
  background: #222;
  top: -15px;
}

.tv-antenna:nth-child(2) {
  left: 30%;
  transform: rotate(-15deg);
}
.tv-antenna:nth-child(3) {
  right: 30%;
  transform: rotate(15deg);
}

.nokia-screen {
  position: absolute;
  width: 80%;
  height: 40%;
  background: #73839a;
  top: 10%;
  left: 10%;
}

.nokia-keypad {
  position: absolute;
  width: 80%;
  height: 40%;
  background: #555;
  bottom: 10%;
  left: 10%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: 1px;
}

@keyframes tvScan {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}

@keyframes glitchMove {
  0% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-2px);
  }
  50% {
    transform: translateX(2px);
  }
  75% {
    transform: translateX(-1px);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
